import React from 'react';
import RepProfileCallDaySummary from './day-summary';
import RepProfileCallDayDetails from './RepProfileCallDayDetails';
import LoadingOverlay from 'components/pure/utils/LoadingOverlay';
import RepProfileSectionHeader from './RepProfileSectionHeader';
import './RepProfileCalls.styl';
import {List, Map} from 'immutable';
import moment from 'moment';

const weekendDays = ['Saturday', 'Sunday'];
function isWeekend(day) {
  return weekendDays.includes(day.format('dddd'));
}

export default class RepProfileCalls extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      showWeekends: false,
    };
    this.handleShowWeekendsToggled = this.handleShowWeekendsToggled.bind(this);
  }

  handleShowWeekendsToggled(toggled) {
    // XXX can remove the props check once rep profile is in dashV2.
    // In dashV2 we use only the state in this component.
    if (this.props.onToggleShowWeekends) {
      this.props.onToggleShowWeekends(toggled);
    } else {
      this.setState({showWeekends: !this.state.showWeekends});
    }
  }

  getCallDetails(userId, date) {
    this.props.getCallDetails(userId, date).then(([, , callDetails]) =>
      this.props.getInfoAboutFiles({
        file_upload_ids: callDetails.map(detail => detail.id),
      })
    );
  }

  getDayBuckets(rep, teamConversationTrend, teamCallDurationTrend, isLoading) {
    // fail fast if we dont have data, or we're loading
    if (teamConversationTrend.size === 0 || teamCallDurationTrend.size === 0 || isLoading) {
      return [List(), null, null, null];
    }

    // get the buckets in date desc order
    const bucketKeysInOrder = teamCallDurationTrend
      .first()
      .map(d => d.date)
      .reverse();

    // now convert them to to maps instead just to make things easier
    const teamConvosBucketed = teamConversationTrend.map(trend =>
      Map(trend.map(d => [d.date, d.value]))
    );
    const teamCallDurationBucketed = teamCallDurationTrend.map(trend =>
      Map(trend.map(d => [d.date, d.value]))
    );

    const teamConvosPivotedByDay = Map(
      bucketKeysInOrder.map(k => {
        // per rep here - map through the reps and get their day buckets to sum up
        const totalForDay = teamConvosBucketed
          .map(trend => trend.get(k) || 0)
          .reduce((sum, s) => sum + s, 0);

        return [k, totalForDay];
      })
    );

    const teamCallDurationPivotedByDay = Map(
      bucketKeysInOrder.map(k => {
        // per rep here - map through the reps and get their day buckets to sum up
        const totalForDay = teamCallDurationBucketed
          .map(trend => trend.get(k) || 0)
          .reduce((sum, s) => sum + s, 0);

        return [k, totalForDay];
      })
    );

    // the maps can be sparse if no data for a rep. so be defensive
    const callDurationForRep = teamCallDurationBucketed.get(rep.id);
    const teamConvosForRep = teamConvosBucketed.get(rep.id);

    const bucketData = bucketKeysInOrder.map(k => {
      return {
        callDurationSeconds: callDurationForRep ? callDurationForRep.get(k) : 0,
        callsPlaced: teamConvosForRep ? teamConvosForRep.get(k) : 0,
        date: moment(k),
        teamCallDurationSeconds: teamCallDurationPivotedByDay.get(k),
        teamCallsPlaced: teamConvosPivotedByDay.get(k),
      };
    });

    // used to calculate how the day fared vs the day average for the time period
    const avgCallDurationPerDay =
      teamCallDurationPivotedByDay.reduce((sum, s) => sum + s, 0) /
      teamCallDurationPivotedByDay.size;

    const maxCallsPlaced = teamConversationTrend
      .toSet()
      .flatten()
      .map(t => t.value)
      .max();

    const maxDurationInSeconds = teamCallDurationTrend
      .toSet()
      .flatten()
      .map(t => t.value)
      .max();

    return [bucketData, avgCallDurationPerDay, maxCallsPlaced, maxDurationInSeconds];
  }

  render() {
    const {rep, teamConversationTrend, teamCallDurationTrend, isLoading} = this.props;

    // if this page is loaded as the first page, rep is not populated initially, but will in the next cycle
    if (!rep) return null;

    // XXX can remove the props check once rep profile is in dashV2.
    // In dashV2 we use only the state in this component.
    const showWeekends = this.props.onToggleShowWeekends
      ? this.props.showWeekends
      : this.state.showWeekends;

    const [dayBuckets, averageCallDurationPerDay, maxCallsPlaced, maxCallDurationSeconds] =
      this.getDayBuckets(rep, teamConversationTrend, teamCallDurationTrend, isLoading);

    const filteredDayBuckets = dayBuckets.filter(d => (showWeekends ? true : !isWeekend(d.date)));

    return filteredDayBuckets || isLoading ? (
      <section className="rep-profile-calls main-section">
        {isLoading && <LoadingOverlay />}
        {rep && (
          <RepProfileSectionHeader
            rep={rep}
            title={(rep ? `${rep.firstName}'s ` : '') + 'Calls'}
            toggled={showWeekends}
            isToggleDisabled={this.props.isLoading}
            handleToggle={this.handleShowWeekendsToggled}
            toggledLabel="Show Weekends"
            unToggledLabel="Ignore Weekends"
            startDate={this.props.headerStartDate}
            endDate={this.props.headerEndDate}
            location={this.props.location}
          />
        )}
        {filteredDayBuckets.size > 0 ? (
          <ol className="call-list">
            {filteredDayBuckets.map(dayBucket => (
              <li key={dayBucket.date.format()}>
                <RepProfileCallDaySummary
                  rep={rep}
                  maxCallsPlaced={maxCallsPlaced}
                  maxCallDurationSeconds={maxCallDurationSeconds}
                  expanded={this.props.openSummaries.get(dayBucket.date)}
                  dayBucket={dayBucket}
                  callDurationSecondsMean={averageCallDurationPerDay}
                  openCallDetails={(userId, date) => {
                    this.props.openCallDetails(userId, date);
                    const opened = this.props.openSummaries.get(dayBucket.date);
                    if (!opened && dayBucket.callsPlaced > 0) {
                      this.getCallDetails(userId, date);
                    }
                  }}
                />
                {this.props.openSummaries.get(dayBucket.date) && (
                  <RepProfileCallDayDetails
                    callDetails={this.props.callDetails.get(dayBucket.date)}
                    callDetailsLoading={this.props.callDetailsLoading.get(dayBucket.date)}
                    callDetailsFailed={this.props.callDetailsFailed.get(dayBucket.date)}
                  />
                )}
              </li>
            ))}
          </ol>
        ) : (
          <div className="empty">
            <p>No call data for {rep.getFullName()}</p>
          </div>
        )}
      </section>
    ) : null;
  }
}
